import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, Field } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { useAuthStore } from '../../stores/auth.store';
import { fmtEur, fmtDate } from '../../lib/utils';
import {
  TAX_KINDS, TAX_KIND_LABEL, TAX_SOURCE_LABEL,
  type TaxKind, type TaxSource,
} from '@masamune/shared';
import { isSuperAdmin, type Entity, type TaxObligation } from '../../features/admin/adminApi';

/** Le fonti che si scelgono a mano: "Da FattureInCloud" la mette solo l'import. */
const SOURCES_A_MANO: TaxSource[] = ['STIMA', 'CALCOLATO', 'CONFERMATO'];

/** "1.035,21", "1035,21" e "1035.21" valgono tutti 1035,21. */
function parseImporto(s: string): number {
  const t = String(s).trim();
  if (!t) return NaN;
  return Number(t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t);
}

interface Prefill {
  kind?: TaxKind;
  year?: number;
  period?: string;
  description?: string;
  amountDue?: number;
  dueDate?: string;
  source?: TaxSource;
  /**
   * 'cifra' = "Inserisci la cifra del commercialista": si chiede solo
   * l'importo (e una nota), e la fonte diventa Confermata. Serve per le stime,
   * per i nostri calcoli e per le scadenze senza cifra (Fisco).
   */
  intent?: 'cifra';
}

/**
 * Tassa da pagare (F24, IVA, imposte della società, rate): tipo, anno/periodo,
 * descrizione, importo, scadenza, da dove viene la cifra (Stima / Calcolata da
 * noi / Confermata), nota. Con `tax` modifica quella che c'è (PATCH): con una
 * rata sola, anche già pagata, la rata segue l'importo. "Di chi" lo sceglie
 * solo il Super Admin. Le rate si aggiungono dall'API (/taxes/:id/installments):
 * qui una scadenza sola.
 *
 * Con `prefill.intent = 'cifra'` il modale diventa "Inserisci la cifra del
 * commercialista": importo e nota, fonte Confermata. Se la tassa non esiste
 * ancora (l'anticipo IVA di dicembre) la crea con i dati del prefill.
 */
export function NewTaxModal({ onClose, tax, prefill }: { onClose: () => void; tax?: TaxObligation; prefill?: Prefill }) {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const editing = !!tax;
  const cifra = prefill?.intent === 'cifra';
  const [form, setForm] = useState({
    kind: (tax?.kind ?? prefill?.kind ?? 'F24_DIPENDENTI') as TaxKind,
    year: String(tax?.year ?? prefill?.year ?? new Date().getFullYear()),
    period: tax?.period ?? prefill?.period ?? '',
    description: tax?.description ?? prefill?.description ?? '',
    // Chi inserisce la cifra vera parte da un campo vuoto: la stima non è un punto di partenza.
    amountDue: cifra ? '' : tax ? String(tax.amountDue) : prefill?.amountDue != null ? String(prefill.amountDue) : '',
    dueDate: (tax?.dueDate ?? prefill?.dueDate ?? '').slice(0, 10),
    source: (cifra ? 'CONFERMATO' : tax?.source ?? prefill?.source ?? 'CONFERMATO') as TaxSource,
    note: tax?.note ?? '',
    entity: (tax?.entity ?? 'MASAMUNE') as Entity,
  });
  // Senza cifra si può creare lo stesso (la scadenza è certa, l'importo arriva dopo): in pagina risulta "Manca la cifra".
  const amount = !cifra && !String(form.amountDue).trim() ? 0 : parseImporto(form.amountDue);
  const nome = form.description || `${TAX_KIND_LABEL[form.kind]} ${form.year}`;

  const payload = () => ({
    kind: form.kind,
    year: Number(form.year),
    period: form.period.trim() || null,
    description: form.description.trim() || `${TAX_KIND_LABEL[form.kind]} ${form.year}`,
    amountDue: amount,
    dueDate: form.dueDate || null,
    source: form.source,
    note: form.note.trim() || null,
    entity: form.entity,
  });

  const save = useMutation({
    mutationFn: () => {
      if (editing && cifra) return api.patch(`/admin/taxes/${tax!.id}`, { amountDue: amount, source: 'CONFERMATO', note: form.note.trim() || null });
      return editing
        ? api.patch(`/admin/taxes/${tax!.id}`, payload())
        : api.post<{ id: string }>('/admin/taxes', { ...payload(), status: 'APERTA' });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
      toast.ok(cifra ? 'Cifra del commercialista inserita' : editing ? 'Tassa aggiornata' : 'Tassa creata', `${nome} · € ${fmtEur(amount, true)}`);
      onClose();
    },
    onError: (err) => toast.alert(cifra ? 'Cifra del commercialista non salvata' : 'Tassa non salvata', err instanceof ApiError ? err.message : 'Riprova'),
  });

  const canSubmit = Number.isFinite(amount) && (cifra ? amount > 0 : amount >= 0) && !!form.year && !save.isPending;
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && canSubmit) { e.preventDefault(); save.mutate(); }
  };

  if (cifra) {
    return (
      <Modal
        title="Inserisci la cifra del commercialista"
        kicker="Amministrazione · Fisco"
        onClose={onClose}
        size="md"
        footer={
          <>
            <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
            <div className="spacer" />
            <button className="btn btn-primary" onClick={() => save.mutate()} disabled={!canSubmit}>
              Salva la cifra <Icon name="arrowRight" size={12} />
            </button>
          </>
        }
      >
        <div className="form-grid" onKeyDown={onKeyDown}>
          <Field label="Tassa" span={12}>
            <div className="font-semibold">{nome}</div>
            <div className="text-[12px] mute">
              {form.dueDate ? `Da pagare entro il ${fmtDate(form.dueDate)}` : 'Senza scadenza'}
              {tax && tax.amountDue > 0 && ` · oggi a sistema € ${fmtEur(tax.amountDue, true)}, ${TAX_SOURCE_LABEL[tax.source].toLowerCase()}`}
            </div>
          </Field>
          <Field label="Importo dell'F24 del commercialista" required span={12} hint="La cifra scritta nell'F24 o nella mail dello studio. Da qui in poi la cifra risulta Confermata.">
            <input className="inp mono" inputMode="decimal" placeholder="0,00" autoFocus value={form.amountDue} onChange={(e) => setForm({ ...form, amountDue: e.target.value })} />
          </Field>
          <Field label="Nota" span={12}>
            <textarea className="inp" rows={2} placeholder="Facoltativa: per esempio da quale mail o F24 viene la cifra" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </Field>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={editing ? 'Modifica la tassa da pagare' : 'Nuova tassa da pagare'}
      kicker="Amministrazione · Fisco"
      onClose={onClose}
      size="md"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-primary" onClick={() => save.mutate()} disabled={!canSubmit}>
            {editing ? 'Salva le modifiche' : 'Crea la tassa'} <Icon name="arrowRight" size={12} />
          </button>
        </>
      }
    >
      <div className="form-grid" onKeyDown={onKeyDown}>
        <Field label="Tipo di tassa" span={7}>
          <select className="inp" autoFocus value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as TaxKind })}>
            {TAX_KINDS.map((k) => <option key={k} value={k}>{TAX_KIND_LABEL[k]}</option>)}
          </select>
        </Field>
        <Field label="Anno" required span={5}>
          <input className="inp mono" inputMode="numeric" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} />
        </Field>
        <Field label="Descrizione" span={8} hint="Facoltativa: se vuota usa tipo e anno">
          <input className="inp" placeholder="Es. IVA di luglio-settembre 2026" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>
        <Field label="Periodo" span={4} hint="Es. 2026-Q3 (luglio-settembre) o 2026-08 (agosto)">
          <input className="inp mono" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} />
        </Field>
        <Field label="Importo da versare" span={6} hint="Se non la sai ancora lasciala vuota: in pagina risulta Manca la cifra.">
          <input className="inp mono" inputMode="decimal" placeholder="0,00" value={form.amountDue} onChange={(e) => setForm({ ...form, amountDue: e.target.value })} />
        </Field>
        <Field label="Da pagare entro il" span={6}>
          <input className="inp mono" type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} />
        </Field>
        <Field label="Da dove viene la cifra" span={12} hint="Confermata solo se c'è l'F24 o la mail del commercialista. Calcolata da noi = dalle fatture.">
          <div className="seg-control">
            {SOURCES_A_MANO.map((s) => (
              <button key={s} type="button" aria-pressed={form.source === s} onClick={() => setForm({ ...form, source: s })}>{TAX_SOURCE_LABEL[s]}</button>
            ))}
          </div>
        </Field>
        {isSuperAdmin(me) && (
          <Field label="Di chi è la tassa" span={12}>
            <div className="seg-control">
              <button type="button" aria-pressed={form.entity === 'MASAMUNE'} onClick={() => setForm({ ...form, entity: 'MASAMUNE' })}>Di Masamune</button>
              <button type="button" aria-pressed={form.entity === 'PERSONALE_ANDREA'} onClick={() => setForm({ ...form, entity: 'PERSONALE_ANDREA' })}>Di Andrea</button>
            </div>
          </Field>
        )}
        <Field label="Nota" span={12}>
          <textarea className="inp" rows={2} placeholder="Facoltativa" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        </Field>
      </div>
    </Modal>
  );
}
